import React, { useState, useEffect, useMemo } from 'react'
import {
  Button,
  Icon,
  Spin,
  message,
  Alert,
  Tooltip,
  Modal,
  Progress
} from 'antd'
import NewHeader from '../../../../components/NewHeader'
import './DraftModePage.scss'
import history from '../../../../history'

// Reuse existing components
import Voiceover from '../Voiceover'
import VideoStyle from '../VideoStyle'
import AspectRatioSelector from '../VideoStyle/components/AspectRatioSelector'
import CharacterMappingTable from '../ShotList/components/CharacterMappingTable'
import StepTitle from '../StepTitle'
import AddCharacterModal from '../ShotList/components/CharacterMappingTable/AddCharacterModal'

const DraftModePage = ({
  formData,
  setFormData,
  onExitDraftMode,
  onFinishDraft,
  voices,
  setVoices,
  onVoiceChange,
  characters,
  isFetchingCharacters,
  fetchCharacters,
  draftShotsHook,
  videoId
}) => {
  const [draftState, setDraftState] = useState({
    selectedVoice: formData.selectedVoice || null,
    videoStyle: formData.videoStyle || null,
    aspectRatio: formData.aspectRatio || '16:9',
    changedCharacters: formData.selectedCharacter || [],
    characterMap: formData.characterMap || null,
    locationMap: formData.locationMap || null,
    shotList: formData.shotList || [],
    shotGenerationStyle: formData.shotGenerationStyle || null,
    isShotsReady: formData.shotList && formData.shotList.length > 0,
    audioMode: formData.audioMode || 'narration',
    characterVoices: formData.characterVoices || {},
    wardrobe: formData.wardrobe || null,
    outfitMap: formData.outfitMap || null,
    propMap: formData.propMap || null,
    props: formData.props || null,
    outfits_reference: formData.outfits_reference || null,
    videoId: videoId
  })

  const [showAddCharacterModal, setShowAddCharacterModal] = useState(false)

  const {
    isFetchingShots,
    fetchDraftShots,
    shotsData,
    shotsGenerationProgress,
    draftGenerationProgress,
    error
  } = draftShotsHook

  // Start shots generation on mount (REMOVED - now waits for style selection)
  useEffect(() => {
    // If we already have a style selected (e.g. returning to draft mode), trigger fetch
    const selectedStyleId =
      draftState.shotGenerationStyle?.key || draftState.shotGenerationStyle?.id

    if (
      selectedStyleId &&
      formData.voiceoverText &&
      !draftState.isShotsReady &&
      !isFetchingShots &&
      !shotsData &&
      (!formData.shotList || formData.shotList.length === 0)
    ) {
      fetchDraftShots(
        formData.voiceoverText,
        selectedStyleId,
        draftState.audioMode,
        formData.language || 'en'
      )
    }
  }, [])

  // Update state when shots arrive
  useEffect(() => {
    if (shotsData) {
      setDraftState(prev => ({
        ...prev,
        characterMap: shotsData.characterMap,
        locationMap: shotsData.locationMap,
        shotList: shotsData.shotList,
        wardrobe: shotsData.wardrobe,
        outfitMap: shotsData.outfitMap,
        propMap: shotsData.propMap,
        props: shotsData.props,
        ...(shotsData.characterVoices
          ? { characterVoices: shotsData.characterVoices }
          : {}),
        ...(shotsData.selectedVoice
          ? { selectedVoice: shotsData.selectedVoice }
          : {}),
        outfits_reference: shotsData.outfits_reference,
        isShotsReady: true
      }))

      // Persist to parent formData immediately
      setFormData(prev => ({
        ...prev,
        shotList: shotsData.shotList,
        characterMap: shotsData.characterMap,
        locationMap: shotsData.locationMap,
        wardrobe: shotsData.wardrobe,
        outfitMap: shotsData.outfitMap,
        propMap: shotsData.propMap,
        outfits_reference: shotsData.outfits_reference,
        props: shotsData.props,
        ...(shotsData.characterVoices
          ? { characterVoices: shotsData.characterVoices }
          : {}),
        ...(shotsData.selectedVoice
          ? { selectedVoice: shotsData.selectedVoice }
          : {}),
        shotGenerationStyle: draftState.shotGenerationStyle
      }))
    }
  }, [shotsData, error])

  const handleVoiceSelect = (voice, shouldClone, characterKey) => {
    if (characterKey) {
      setDraftState(prev => ({
        ...prev,
        characterVoices: {
          ...(prev.characterVoices || {}),
          [characterKey]: voice
        }
      }))
    } else {
      setDraftState(prev => ({ ...prev, selectedVoice: voice }))
    }
  }

  const handleAudioModeChange = mode => {
    setDraftState(prev => ({ ...prev, audioMode: mode }))
  }

  const handleRatioChange = ratio => {
    setDraftState(prev => ({ ...prev, aspectRatio: ratio }))
  }

  const handleStyleChange = styleObj => {
    setDraftState(prev => ({ ...prev, videoStyle: styleObj }))
  }

  const handleShotStyleChange = styleObj => {
    setDraftState(prev => ({
      ...prev,
      shotGenerationStyle: styleObj,
      isShotsReady: false // Reset ready state to show loading/waiting
    }))

    // Trigger shot generation immediately when style is selected/changed
    const styleId = styleObj?.key || styleObj?.id
    if (styleId && formData.voiceoverText) {
      fetchDraftShots(
        formData.voiceoverText,
        styleId,
        draftState.audioMode,
        formData.language || 'en'
      )
    }
  }

  const handleCharacterChange = (original, current, firstChar, characterId) => {
    const originalFormatted = `__${original}__`
    const currentFormatted = `__${current}__`

    const newItem = {
      tag: characterId,
      last: originalFormatted,
      current: currentFormatted,
      original: originalFormatted
    }

    setDraftState(prev => {
      const newChanged = [...prev.changedCharacters]
      const existingIndex = newChanged.findIndex(
        c => c.original === originalFormatted
      )

      if (existingIndex >= 0) {
        newChanged[existingIndex] = newItem
      } else {
        newChanged.push(newItem)
      }

      // Also sync to parent formData so auto-save persists to API
      setFormData(prevForm => {
        const formChanged = [...(prevForm.selectedCharacter || [])]
        const formIndex = formChanged.findIndex(
          c => c.original === originalFormatted
        )
        if (formIndex >= 0) {
          formChanged[formIndex] = newItem
        } else {
          formChanged.push(newItem)
        }
        return {
          ...prevForm,
          selectedCharacter: formChanged,
          wardrobe: prev.wardrobe,
          outfitMap: prev.outfitMap,
          outfits_reference: prev.outfits_reference
        }
      })

      return { ...prev, changedCharacters: newChanged }
    })
  }

  const handleCharacterAddition = () => {
    if (fetchCharacters) {
      fetchCharacters(true)
    }
  }

  const handleWardrobeSave = (characterKey, updatedTagsData) => {
    setDraftState(prev => ({
      ...prev,
      outfits_reference: {
        ...(prev.outfits_reference || {}),
        [characterKey]: updatedTagsData
      }
    }))

    // Sync to parent formData so auto-save persists to API
    setFormData(prev => ({
      ...prev,
      outfits_reference: {
        ...(prev.outfits_reference || {}),
        [characterKey]: updatedTagsData
      }
    }))
  }

  // Check if all outfits are mapped (similar to ShotList.js)
  const allOutfitsMapped = useMemo(() => {
    const storyCharacters = draftState.characterMap
      ? Object.keys(draftState.characterMap)
      : []
    if (storyCharacters.length === 0) return true
    const wardrobe = draftState.wardrobe || {}
    const outfitsRef = draftState.outfits_reference || {}

    return storyCharacters.every(char => {
      const charOutfits = wardrobe[char] || []
      if (charOutfits.length === 0) return true
      const charRefs = outfitsRef[char] || {}
      const refCount = Object.keys(charRefs).filter(key => charRefs[key]).length
      return refCount === charOutfits.length
    })
  }, [
    draftState.characterMap,
    draftState.wardrobe,
    draftState.outfits_reference
  ])
  const isReadyToCreate = useMemo(() => {
    const hasVoice = !!draftState.selectedVoice
    const hasStyle = !!draftState.videoStyle
    const hasShotStyle =
      draftState.shotGenerationStyle &&
      (draftState.shotGenerationStyle.id || draftState.shotGenerationStyle.key)

    const storyCharacters = draftState.characterMap
      ? Object.keys(draftState.characterMap)
      : []

    // Character mapping validation (Step 3)
    const unmappedChars = storyCharacters.filter(storyChar => {
      return !draftState.changedCharacters.some(c =>
        c.original.includes(storyChar)
      )
    })

    // Character voice mapping validation (Step 1 - Advanced Mode)
    const unmappedCharacterVoices =
      draftState.audioMode === 'dialogue'
        ? storyCharacters.filter(key => !draftState.characterVoices?.[key])
        : []

    return (
      hasVoice &&
      hasStyle &&
      hasShotStyle &&
      unmappedChars.length === 0 &&
      unmappedCharacterVoices.length === 0 &&
      draftState.isShotsReady &&
      allOutfitsMapped
    )
  }, [draftState, allOutfitsMapped])

  const [isDraftCreating, setIsDraftCreating] = useState(
    formData.draft?.id && formData.draft?.status === 'pending'
  )
  const [draftTaskId, setDraftTaskId] = useState(
    formData.draft?.status === 'pending' ? formData.draft.id : null
  )

  // Polling effect
  useEffect(() => {
    if (draftTaskId) {
      setIsDraftCreating(true)
      draftShotsHook
        .pollDraftVideo(draftTaskId, output => {
          message.success('Draft video created successfully!')
          onFinishDraft(output)
        })
        .catch(() => {
          setIsDraftCreating(false)
          setDraftTaskId(null)
          message.error('Failed to create draft video.')
          setFormData(prev => ({
            ...prev,
            draft: {
              ...(prev.draft || {}),
              status: 'error'
            }
          }))
        })
    }
  }, [draftTaskId])

  const handleCreateVideo = () => {
    if (!isReadyToCreate) {
      const storyCharacters = draftState.characterMap
        ? Object.keys(draftState.characterMap)
        : []
      const unmappedChars = storyCharacters.filter(storyChar => {
        return !draftState.changedCharacters.some(c =>
          c.original.includes(storyChar)
        )
      })

      const unmappedCharacterVoices =
        draftState.audioMode === 'dialogue'
          ? storyCharacters.filter(key => !draftState.characterVoices?.[key])
          : []

      Modal.info({
        title: 'Draft Requirements Incomplete',
        centered: true,
        width: '600px',
        content: (
          <div className="validation-modal-content">
            <p>Please complete the following steps to create your video:</p>
            <ul style={{ listStyle: 'none', padding: 0, marginTop: '16px' }}>
              <li
                style={{
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Icon
                  type={
                    !!draftState.selectedVoice ? 'check-circle' : 'close-circle'
                  }
                  theme="filled"
                  style={{
                    marginRight: '10px',
                    fontSize: '18px',
                    color: !!draftState.selectedVoice ? '#52c41a' : '#ff4d4f'
                  }}
                />
                <span>Select a Narrator Voice</span>
              </li>

              {draftState.audioMode === 'dialogue' && (
                <li
                  style={{
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Icon
                    type={
                      unmappedCharacterVoices.length === 0
                        ? 'check-circle'
                        : 'close-circle'
                    }
                    theme="filled"
                    style={{
                      marginRight: '10px',
                      fontSize: '18px',
                      color:
                        unmappedCharacterVoices.length === 0
                          ? '#52c41a'
                          : '#ff4d4f'
                    }}
                  />
                  <span>
                    Map All Character Voices{' '}
                    {unmappedCharacterVoices.length > 0 &&
                      `(${unmappedCharacterVoices.length} remaining)`}
                  </span>
                </li>
              )}
              <li
                style={{
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Icon
                  type={
                    !!draftState.videoStyle ? 'check-circle' : 'close-circle'
                  }
                  theme="filled"
                  style={{
                    marginRight: '10px',
                    fontSize: '18px',
                    color: !!draftState.videoStyle ? '#52c41a' : '#ff4d4f'
                  }}
                />
                <span>Select a Visual Style</span>
              </li>
              <li
                style={{
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Icon
                  type={
                    draftState.shotGenerationStyle &&
                    (draftState.shotGenerationStyle.id ||
                      draftState.shotGenerationStyle.key)
                      ? 'check-circle'
                      : 'close-circle'
                  }
                  theme="filled"
                  style={{
                    marginRight: '10px',
                    fontSize: '18px',
                    color:
                      draftState.shotGenerationStyle &&
                      (draftState.shotGenerationStyle.id ||
                        draftState.shotGenerationStyle.key)
                        ? '#52c41a'
                        : '#ff4d4f'
                  }}
                />
                <span>Select an Editing Style</span>
              </li>
              <li
                style={{
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Icon
                  type={
                    draftState.isShotsReady && unmappedChars.length === 0
                      ? 'check-circle'
                      : 'close-circle'
                  }
                  theme="filled"
                  style={{
                    marginRight: '10px',
                    fontSize: '18px',
                    color:
                      draftState.isShotsReady && unmappedChars.length === 0
                        ? '#52c41a'
                        : '#ff4d4f'
                  }}
                />
                <span>
                  Map All Characters{' '}
                  {unmappedChars.length > 0 &&
                    `(${unmappedChars.length} remaining)`}
                </span>
              </li>
              <li
                style={{
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Icon
                  type={
                    draftState.isShotsReady && allOutfitsMapped
                      ? 'check-circle'
                      : 'close-circle'
                  }
                  theme="filled"
                  style={{
                    marginRight: '10px',
                    fontSize: '18px',
                    color:
                      draftState.isShotsReady && allOutfitsMapped
                        ? '#52c41a'
                        : '#ff4d4f'
                  }}
                />
                <span>Map All Outfits</span>
              </li>
            </ul>
          </div>
        )
      })
      return
    }

    Modal.confirm({
      title: 'Create Draft Video',
      content:
        'Are you sure you want to create the draft video? This action will start the generation process.',
      onOk: async () => {
        try {
          setIsDraftCreating(true)

          // Construct payload locally for the API call
          const draftPayload = {
            id: videoId,
            voiceover_text: formData.voiceoverText,
            selected_character: draftState.changedCharacters.map(c => ({
              last: c.original,
              current: c.current,
              original: c.original,
              character_id: c.tag
            })),
            shot_list_data_id: null,
            video_style: draftState.videoStyle,
            shot_loading: false,
            video_url: '',
            location_map: JSON.stringify(draftState.locationMap),
            location_reference: 'null',
            character_map: JSON.stringify(draftState.characterMap),
            selected_voice: draftState.selectedVoice,
            audio_mode: draftState.audioMode,
            character_voices: JSON.stringify(draftState.characterVoices || {}),
            shot_generation_style:
              draftState.shotGenerationStyle?.key ||
              draftState.shotGenerationStyle?.id ||
              (typeof draftState.shotGenerationStyle === 'string'
                ? draftState.shotGenerationStyle
                : null),
            aspect_ratio: draftState.aspectRatio,
            creator_id: 248,
            organization_id: 96,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            deleted_at: null,
            shot_list_data: {},
            shot_list: draftState.shotList,
            wardrobe: draftState.wardrobe
              ? JSON.stringify(draftState.wardrobe)
              : null,
            outfit_map: draftState.outfitMap
              ? JSON.stringify(draftState.outfitMap)
              : null,
            outfits_reference: draftState.outfits_reference
              ? JSON.stringify(draftState.outfits_reference)
              : null,
            props: draftState.props ? JSON.stringify(draftState.props) : null,
            prop_map: draftState.propMap
              ? JSON.stringify(draftState.propMap)
              : null
          }

          // Call API directly
          const {
            generateDraftVideo
          } = require('../../../../services/api/aiVideoGeneration')
          const res = await generateDraftVideo({
            draft_payload: draftPayload,
            video_id: draftState.videoId
          })
          const taskId = res?.data?.task_id

          if (taskId) {
            setDraftTaskId(taskId)

            // Update formData locally, which triggers persistence save
            setFormData(prev => ({
              ...prev,
              selectedVoice: draftState.selectedVoice,
              videoStyle: draftState.videoStyle,
              aspectRatio: draftState.aspectRatio,
              shotList: draftState.shotList,
              audioMode: draftState.audioMode,
              characterVoices: draftState.characterVoices,
              shotGenerationStyle: draftState.shotGenerationStyle,
              locationMap: draftState.locationMap,
              characterMap: draftState.characterMap,
              characterList: draftState.changedCharacters.map(c => c.current),
              draft: {
                id: taskId,
                status: 'pending'
              }
            }))

            // Polling is handled by the useEffect above reacting to draftTaskId
          } else {
            throw new Error('No task ID received')
          }
        } catch (error) {
          console.error(error)
          message.error('Failed to start draft creation')
          setIsDraftCreating(false)
        }
      }
    })
  }

  // --- Render Helpers ---

  const mockFormData = useMemo(
    () => ({
      ...formData,
      ...draftState,
      audioMode: draftState.audioMode,
      characterVoices: draftState.characterVoices,
      shotList: draftState.shotList || []
    }),
    [formData, draftState]
  )

  console.log({ draftState })

  return (
    <div className="draft-mode-page">
      <div className="video-generation__header">
        <div className="video-generation__header_top">
          <div className="video-generation__header_info">
            <div
              className="video-generation__header_info-icon"
              onClick={() => {
                if (isDraftCreating) {
                  history.push('/video-create')
                } else {
                  onExitDraftMode()
                }
              }}
            >
              <Tooltip
                title={
                  isDraftCreating ? 'Back to Video List' : 'Back to Manual Mode'
                }
              >
                <Icon type="arrow-left" />
              </Tooltip>
            </div>
            <div className="video-generation__header_info-text">
              <div className="header-container-text">
                <h1 className="header-title-text">Draft Mode Configuration</h1>
              </div>
              <p className="header-subtitle-text"></p>
            </div>
          </div>
          <NewHeader />
        </div>
      </div>
      {isDraftCreating ? (
        <div className="draft-processing-state">
          <div className="processing-content">
            <div className="processing-visual">
              <div className="custom-loader" />
              <div className="loader-center">
                <div className="inner-dot" />
              </div>
            </div>
            <div className="text-content">
              <h2>Creating Your Draft Video...</h2>
              <p>
                Our AI engine is generating scenes, synthesizing visuals, and
                assembling your draft video. This process typically takes a few
                minutes depending on the complexity of your story.
              </p>
            </div>
            {draftGenerationProgress > 0 && (
              <div className="draft-progress-container">
                <Progress
                  percent={Math.round(draftGenerationProgress)}
                  status="active"
                />
              </div>
            )}
            <div className="processing-info-custom">
              <div className="info-icon-wrapper">
                <Icon type="mail" theme="filled" />
              </div>
              <div className="info-text">
                <h4>Email Notification</h4>
                <p>
                  You can safely navigate away or close this page. Once your
                  draft video is complete, we'll send you an email notification.
                  You can then return to review and finalize your video.
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="draft-scroll-container">
            {/* 1. Voiceover Selection */}
            <div className="draft-section">
              <div className="section-title">
                <span className="number-badge">1</span>
                <h3>Step 1: Voiceover</h3>
              </div>

              <Voiceover
                formData={mockFormData}
                setFormData={updateFunc => {
                  const updated = updateFunc(mockFormData)
                  if (updated.selectedVoice !== draftState.selectedVoice) {
                    handleVoiceSelect(updated.selectedVoice)
                  }
                  if (updated.audioMode !== draftState.audioMode) {
                    handleAudioModeChange(updated.audioMode)
                  }
                  if (updated.characterVoices !== draftState.characterVoices) {
                    setDraftState(prev => ({
                      ...prev,
                      characterVoices: updated.characterVoices
                    }))

                    setFormData(prev => ({
                      ...prev,
                      characterVoices: updated.characterVoices
                    }))
                  }
                }}
                onValidationChange={() => {}}
                voices={voices}
                setVoices={setVoices}
                onVoiceChange={handleVoiceSelect}
                isDraftMode={true}
                isShotsReady={draftState.isShotsReady}
                shotGenerationStyle={draftState.shotGenerationStyle}
                shotsGenerationProgress={shotsGenerationProgress}
                isClusteringAudio={false}
                audioClusteringStatus={null}
                audioClusteringProgress={0}
                videoId={videoId}
              />
            </div>

            {/* 2. Video Style */}
            <div className="draft-section">
              <div className="section-title">
                <span className="number-badge">2</span>
                <h3>Step 2: Visual Style</h3>
              </div>

              <VideoStyle
                isDraftMode={true}
                formData={mockFormData}
                setFormData={updateFunc => {
                  const updated = updateFunc(mockFormData)
                  if (updated.videoStyle !== draftState.videoStyle) {
                    handleStyleChange(updated.videoStyle)
                  }
                  if (
                    updated.shotGenerationStyle !==
                    draftState.shotGenerationStyle
                  ) {
                    handleShotStyleChange(updated.shotGenerationStyle)
                  }
                  if (updated.aspectRatio !== draftState.aspectRatio) {
                    handleRatioChange(updated.aspectRatio)
                  }
                }}
                onValidationChange={() => {}}
                onStyleChange={style => handleStyleChange(style)}
                onStyleSelect={style => handleShotStyleChange(style)}
                videoId={videoId}
              />
            </div>

            {/* 3. Character Mapping */}
            <div className="draft-section" style={{ marginBottom: '200px' }}>
              <div className="section-title">
                <span className="number-badge">3</span>
                <h3>Step 3: Character Mapping</h3>
              </div>

              {!draftState.shotGenerationStyle?.key &&
              !draftState.shotGenerationStyle?.id &&
              (!draftState.shotList || draftState.shotList.length === 0) ? (
                <div className="character-loading-state waiting-state">
                  <div className="processing-visual">
                    <div className="style-selection-icon">
                      <Icon
                        type="edit"
                        style={{ fontSize: '48px', color: '#0047ff' }}
                      />
                    </div>
                  </div>
                  <h3>Waiting for Style Selection</h3>
                  <p>
                    Please select an Editing Style in Step 2 to generate shots
                    and identify characters.
                  </p>
                </div>
              ) : !draftState.isShotsReady ? (
                <div className="character-loading-state">
                  <div className="processing-visual">
                    <div className="custom-loader" />
                    <div className="loader-center">
                      <div className="inner-dot" />
                    </div>
                  </div>
                  <p>Analyzing story and identifying characters...</p>
                  {shotsGenerationProgress > 0 && (
                    <div className="shots-progress-container">
                      <Progress
                        percent={Math.round(shotsGenerationProgress)}
                        status="active"
                        size="small"
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  {!draftState.characterMap ||
                  Object.keys(draftState.characterMap).length === 0 ? (
                    <Alert
                      message="No characters detected in this story."
                      type="info"
                    />
                  ) : (
                    <CharacterMappingTable
                      isDraftMode
                      videoId={videoId}
                      formData={mockFormData}
                      setFormData={setDraftState}
                      placeholderCharacters={Object.keys(
                        draftState.characterMap || {}
                      )}
                      characterMap={draftState.characterMap}
                      characters={characters}
                      isFetchingCharacters={isFetchingCharacters}
                      onAddCharacter={() => setShowAddCharacterModal(true)}
                      changedCharacters={draftState.changedCharacters}
                      onCharacterChange={handleCharacterChange}
                      isAllCharactersChanged={false}
                      shotList={draftState.shotList}
                      onAddNewCharacter={(id, name) => {
                        setDraftState(prev => ({
                          ...prev,
                          characterMap: {
                            ...prev.characterMap,
                            [id]: name
                          }
                        }))
                      }}
                      onDeleteCharacter={key => {
                        setDraftState(prev => {
                          const newMap = { ...prev.characterMap }
                          delete newMap[key]
                          return {
                            ...prev,
                            characterMap: newMap
                          }
                        })
                      }}
                      wardrobe={draftState.wardrobe}
                      outfitMap={draftState.outfitMap}
                      outfitsReference={draftState.outfits_reference}
                      onWardrobeSave={handleWardrobeSave}
                      fetchCharacters={fetchCharacters}
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="draft-actions">
            <Button onClick={onExitDraftMode}>Cancel</Button>
            <Button
              type="primary"
              size="large"
              onClick={handleCreateVideo}
              disabled={isDraftCreating}
            >
              Create Video
            </Button>
          </div>
        </>
      )}

      {showAddCharacterModal && (
        <AddCharacterModal
          onClose={() => setShowAddCharacterModal(false)}
          onCreate={handleCharacterAddition}
        />
      )}
    </div>
  )
}

export default DraftModePage
